import React, { useCallback, useEffect, useState } from 'react';
import { useField, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';

import { hasFee } from '../../../../../../utils/fee';
import CTAButton from '../../../../../CTAButton/CTAButton';
import DepositFeePrompts from '../DepositFeePrompts/DepositFeePrompts';
import LocalCurrency from '../LocalCurrency/LocalCurrency';

const AmountCTAButton: React.FC = () => {
  const {
    data: { paySystemDetails, voucherInfo }
  } = useWizardContext();

  const {
    input: {
      value: { amount, currency }
    },
    meta: { valid }
  } = useField('deposit', { subscription: { value: true, valid: true } });

  const form = useForm();
  const { hasValidationErrors, pristine } = form.getState();
  const [feePromptVisible, setFeePromptVisible] = useState(false);
  const [localCurrencyPromptVisible, setLocalCurrencyPromptVisible] = useState(false);

  const defaultCountryCurrency = useSelector(select.account.getDefaultCurrencyForCountry);

  const onClickHandler = useCallback(() => {
    const {
      paysysFee: paySystemFee,
      rates: { rates }
    } = paySystemDetails;
    const showFeePrompt = hasFee(amount, paySystemFee) && paySystemFee.paysysFeeApplies;
    const supportedCurrencies = rates.map(({ curr }) => curr);
    const localCurrencyAvailable =
      !voucherInfo &&
      defaultCountryCurrency &&
      supportedCurrencies.includes(defaultCountryCurrency) &&
      defaultCountryCurrency !== currency;

    if (localCurrencyAvailable && valid && !localCurrencyPromptVisible) {
      setLocalCurrencyPromptVisible(true);
    } else if (showFeePrompt && valid) {
      setFeePromptVisible(true);
    } else {
      form.submit();
    }
  }, [
    amount,
    currency,
    defaultCountryCurrency,
    form,
    localCurrencyPromptVisible,
    paySystemDetails,
    valid,
    voucherInfo
  ]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === 'Enter' &&
        (event.target as HTMLElement).tagName.toUpperCase() === 'INPUT'
      ) {
        onClickHandler();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClickHandler]);

  const onConfirm = () => {
    setFeePromptVisible(false);
    form.submit();
  };

  if (!paySystemDetails) {
    return null;
  }

  return (
    <>
      {localCurrencyPromptVisible && (
        <LocalCurrency
          onClose={() => setLocalCurrencyPromptVisible(false)}
          onCloseCallback={onClickHandler}
        />
      )}
      {feePromptVisible && (
        <DepositFeePrompts onConfirm={onConfirm} onClose={() => setFeePromptVisible(false)} />
      )}
      <CTAButton
        type="button"
        onClick={onClickHandler}
        disabled={(pristine && !amount) || hasValidationErrors}
      />
    </>
  );
};

export default AmountCTAButton;
